import { useLanguage } from '@/contexts/LanguageContext';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { motion } from 'framer-motion';

const Hero = () => {
  const { t } = useLanguage();
  const tagline = t('hero.tagline');
  const [head, ...rest] = tagline.split(',');
  const tail = rest.join(',').trim();

  const partners = ['Jaguar Land Rover', 'Volkswagen', 'Škoda Auto', 'IATF 16949', 'ISO 14001', 'ISO 45001'];

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: '#14243A' }}
    >
      {/* Video background — centered, edge-masked */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/Artifex Slovakia (1) - Trim.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          style={{
            WebkitMaskImage:
              'radial-gradient(ellipse 70% 70% at 50% 50%, #000 35%, rgba(0,0,0,0.65) 60%, transparent 100%)',
            maskImage:
              'radial-gradient(ellipse 70% 70% at 50% 50%, #000 35%, rgba(0,0,0,0.65) 60%, transparent 100%)',
          }}
        />

        {/* Darken overlay for contrast */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 75% 75% at 50% 50%, rgba(20,36,58,0.35) 0%, rgba(20,36,58,0.75) 65%, #14243A 100%)',
          }}
        />

        {/* Edge blend to site bg (navy deep) — inward mask vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, #14243A 0%, transparent 18%, transparent 82%, #14243A 100%), linear-gradient(90deg, #14243A 0%, transparent 10%, transparent 90%, #14243A 100%)',
          }}
        />

        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />

        {/* Grain */}
        <div
          className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
      </div>

      {/* Center content */}
      <div className="container-tight relative z-10 w-full flex flex-col items-center justify-center py-24 text-center">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10 inline-flex items-center gap-3"
        >
          <span className="h-px w-10" style={{ backgroundColor: '#949C58' }} />
          <span
            className="font-display text-xs font-semibold uppercase"
            style={{ color: '#949C58', letterSpacing: '0.22em' }}
          >
            {t('hero.eyebrow')}
          </span>
          <span className="h-px w-10" style={{ backgroundColor: '#949C58' }} />
        </motion.div>

        {/* Massive centered headline */}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          className="font-display font-bold text-white max-w-[14ch] mx-auto"
          style={{
            fontSize: 'clamp(3rem, 8.5vw, 7.5rem)',
            lineHeight: 0.96,
            letterSpacing: '-0.035em',
          }}
        >
          <span className="block">{head}</span>
          {tail && (
            <span
              className="block italic font-semibold"
              style={{ color: '#B6BF7B' }}
            >
              {tail}
            </span>
          )}
        </motion.h1>

        {/* Lede */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-10 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto"
          style={{ color: 'rgba(255,255,255,0.78)' }}
        >
          {t('hero.description')}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-12 flex flex-col sm:flex-row gap-3 items-center justify-center"
        >
          <a
            href="#about"
            className="group inline-flex items-center justify-center gap-2 font-display text-sm font-semibold px-8 py-3.5 rounded-full transition-all duration-300 hover:-translate-y-0.5"
            style={{ backgroundColor: '#FFFFFF', color: '#21394F' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#949C58';
              e.currentTarget.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#FFFFFF';
              e.currentTarget.style.color = '#21394F';
            }}
          >
            {t('hero.learnMore')}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href="#contact"
            className="group inline-flex items-center justify-center gap-2 font-display text-sm font-semibold px-8 py-3.5 rounded-full transition-all duration-300 hover:-translate-y-0.5"
            style={{
              color: '#FFFFFF',
              border: '1px solid rgba(255,255,255,0.4)',
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#FFFFFF';
              e.currentTarget.style.color = '#21394F';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#FFFFFF';
            }}
          >
            {t('hero.contactUs')}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
        </motion.div>

        {/* Scroll cue */}
        <motion.a
          href="#about"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="mt-20 inline-flex flex-col items-center gap-2 text-white/50 hover:text-white transition-colors"
        >
          <span
            className="font-display text-[10px] uppercase"
            style={{ letterSpacing: '0.28em' }}
          >
            Scroll
          </span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ArrowDown className="w-4 h-4" />
          </motion.div>
        </motion.a>
      </div>

      {/* Bottom marquee rail */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.9 }}
        className="absolute bottom-0 left-0 right-0 z-10 border-t"
        style={{
          borderColor: 'rgba(255,255,255,0.1)',
          background: 'linear-gradient(180deg, transparent 0%, rgba(20,36,58,0.6) 100%)',
        }}
      >
        <div className="py-5 overflow-hidden">
          <div className="flex gap-12 animate-marquee whitespace-nowrap">
            {[...partners, ...partners].map((p, i) => (
              <span
                key={i}
                className="font-display text-sm md:text-base font-medium uppercase"
                style={{
                  color: 'rgba(255,255,255,0.4)',
                  letterSpacing: '0.2em',
                }}
              >
                {p}
                <span className="mx-8" style={{ color: '#949C58' }}>·</span>
              </span>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
